import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, Injector, inject, OnDestroy } from '@angular/core';
import { BbitArray, BbitI18n, BbitLanguage, BbitString, BbitTranslationType, IBbitI18n } from '@bbitgmbh/bbit.utils';
import { BbitWebShopAlgoliaService, BbitWebShopService } from '@bbitgmbh/webshop.sdk';
import { I18nPipe } from '@bbitgmbh/website.sdk';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FilterDropdownComponent } from './filter-dropdown/filter-dropdown.component';
import { FilterDropdownOverlayRef } from './filter-dropdown/filter-dropdown.overlay-ref';
import { SortDropdownComponent } from './sort-dropdown/sort-dropdown.component';
import { SortDropdownOverlayRef } from './sort-dropdown/sort-dropdown.overlay-ref';

export interface IBbitAlgoliaFilterItem {
  _id: string;
  designation: IBbitI18n;
  values: { _id: string; designation: IBbitI18n; count: number }[];
  totalCount?: number;
}

@Component({
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
  imports: [NgTemplateOutlet, AsyncPipe, I18nPipe],
})
export class FilterBarComponent implements OnDestroy {
  algolia = inject(BbitWebShopAlgoliaService);
  private _webShop = inject(BbitWebShopService);
  private _overlay = inject(Overlay);

  public allFilters = new BehaviorSubject<IBbitAlgoliaFilterItem[]>([]);
  public firstFilters = new BehaviorSubject<IBbitAlgoliaFilterItem[]>([]);
  public availableFilters = new BehaviorSubject<IBbitAlgoliaFilterItem[]>([]);
  public showAllFilters = new BehaviorSubject(false);
  private _unsubscribe = new Subject<void>();
  constructor() {
    this.algolia
      .observeFilterFacets()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((facets) => {
        let filters: IBbitAlgoliaFilterItem[] = [];
        const attributes = this._webShop.getAttributes();
        const attributeValues = this._webShop.getAttributeValues();
        const manufacturers = this._webShop.getManufacturer();
        const calibers = this._webShop.getCalibers();
        for (const key of Object.keys(facets || {})) {
          const attributeId = key.split('.')[1];
          let designation: IBbitI18n | undefined;
          let totalCount = 0;
          const values: IBbitAlgoliaFilterItem['values'] = [];
          switch (attributeId) {
            case 'manufacturer':
              designation = {
                object: {
                  [BbitLanguage.DE_CH]: {
                    [BbitTranslationType.PLURAL]: 'Hersteller',
                  },
                  [BbitLanguage.FR_CH]: {
                    [BbitTranslationType.PLURAL]: 'Fabricant',
                  },
                  [BbitLanguage.EN_US]: {
                    [BbitTranslationType.PLURAL]: 'Manufacturer',
                  },
                },
              };
              Object.keys(facets?.[key] || {}).forEach((valueKey) => {
                const manufacturer = manufacturers.find((o) => BbitString.ensureBeginningHashtag(o._id) === valueKey);
                const count = facets?.[key]?.[valueKey] || 0;
                if (manufacturer && count > 0) {
                  values.push({
                    _id: valueKey,
                    designation: { object: manufacturer.designation },
                    count,
                  });
                  totalCount += count;
                }
              });
              break;
            case 'caliber':
              designation = {
                object: {
                  [BbitLanguage.DE_CH]: {
                    [BbitTranslationType.PLURAL]: 'Kaliber',
                  },
                  [BbitLanguage.FR_CH]: {
                    [BbitTranslationType.PLURAL]: 'Calibre',
                  },
                  [BbitLanguage.EN_US]: {
                    [BbitTranslationType.PLURAL]: 'Caliber',
                  },
                },
              };

              Object.keys(facets?.[key] || {}).forEach((valueKey) => {
                const caliber = calibers.find((o) => BbitString.ensureBeginningHashtag(o._id) === valueKey);
                const count = facets?.[key]?.[valueKey] || 0;
                if (caliber && count > 0) {
                  values.push({
                    _id: valueKey,
                    designation: { object: caliber.designation },
                    count,
                  });
                  totalCount += count;
                }
              });
              break;
            default: {
              const attribute = attributes.find(
                (a) => BbitString.ensureBeginningHashtag(a._id) === key.replace('filter.', ''),
              );
              if (attribute) {
                designation = { object: attribute.designation };
                Object.keys(facets?.[key] || {}).forEach((valueKey) => {
                  const attributeValue = attributeValues.find(
                    (a) => BbitString.ensureBeginningHashtag(a._id) === valueKey,
                  );
                  const count = facets?.[key]?.[valueKey] || 0;
                  if (attributeValue && count > 0) {
                    values.push({
                      _id: valueKey,
                      designation: { object: attributeValue.designation },
                      count,
                    });
                    totalCount += count;
                  }
                });
              }
              break;
            }
          }
          if (designation && values?.length > 0) {
            filters.push({ _id: key, designation, values, totalCount });
          }
        }

        filters = BbitArray.sortBy(filters, (o) => o.totalCount).reverse();

        if (filters.length > 2) {
          this.firstFilters.next(filters.slice(0, 2));
        } else {
          this.firstFilters.next(filters);
        }

        this.allFilters.next(BbitArray.sortBy(filters, (o) => BbitI18n.t(o.designation)));

        this.availableFilters.next(BbitArray.sortBy(filters, (o) => o.totalCount).reverse());
      });
  }

  public ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  public showFilterDropdown(elem: HTMLButtonElement, item: IBbitAlgoliaFilterItem): void {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(elem)
      .withPositions([
        {
          overlayX: 'end',
          overlayY: 'top',
          originX: 'end',
          originY: 'bottom',
        },
      ]);

    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: '',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.close(),
      positionStrategy,
    });

    const overlayRef = this._overlay.create(overlayConfig);
    const overlayRefInstance = new FilterDropdownOverlayRef(overlayRef, item);

    const injector = Injector.create({
      providers: [
        { provide: BbitWebShopAlgoliaService, useValue: this.algolia },
        { provide: BbitWebShopService, useValue: this._webShop },
        { provide: FilterDropdownOverlayRef, useValue: overlayRefInstance },
      ],
    });
    const containerPortal = new ComponentPortal(FilterDropdownComponent, null, injector);

    overlayRef.attach(containerPortal);

    overlayRef.backdropClick().subscribe((): void => {
      overlayRefInstance.close();
    });
  }

  public showSortDropdown(elem: HTMLButtonElement): void {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(elem)
      .withPositions([
        {
          overlayX: 'start',
          overlayY: 'top',
          originX: 'start',
          originY: 'bottom',
        },
      ]);

    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: '',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.close(),
      positionStrategy,
    });

    const overlayRef = this._overlay.create(overlayConfig);
    const overlayRefInstance = new SortDropdownOverlayRef(overlayRef);

    const injector = Injector.create({
      providers: [
        { provide: BbitWebShopAlgoliaService, useValue: this.algolia },
        { provide: BbitWebShopService, useValue: this._webShop },
        { provide: SortDropdownOverlayRef, useValue: overlayRefInstance },
      ],
    });
    const containerPortal = new ComponentPortal(SortDropdownComponent, null, injector);

    overlayRef.attach(containerPortal);

    overlayRef.backdropClick().subscribe((): void => {
      overlayRefInstance.close();
    });
  }
}
